import { useEffect, useState } from 'react';

import { useAuth } from '@/contexts/auth';
import {
  getReportBrandEventProperties,
  identifyPostHogUser,
  registerReportSuperProperties,
  unregisterReportSuperProperties,
} from '@/lib/posthogIdentity';
import {
  ensureReportReadSession,
  startReportReadSessionTracking,
} from '@/lib/reportReadSession';
import { trackReportOpenEvents } from '@/lib/reportOpenEvents';
import { partnerService } from '@/services/partner';

type UsePostHogReportPropertiesParams = {
  reportId?: string | null;
  reportDate?: string | Date | null;
  reportBrand?: string | null;
  reportPartnerId?: string | null;
  reportLang?: string | null;
  enabled?: boolean;
  captureOpenEvents?: boolean;
};

export const usePostHogReportProperties = ({
  reportId,
  reportDate,
  reportBrand,
  reportPartnerId,
  reportLang,
  enabled = true,
  captureOpenEvents = false,
}: UsePostHogReportPropertiesParams) => {
  const { user } = useAuth();
  const [partnerListName, setPartnerListName] = useState<string | null>(null);
  const [lookedUpPartnerId, setLookedUpPartnerId] = useState<string | null>(null);

  const trimmedBrand = reportBrand?.trim() || null;

  useEffect(() => {
    if (trimmedBrand) {
      setPartnerListName(null);
      setLookedUpPartnerId(reportPartnerId ?? '');
      return;
    }

    if (!reportPartnerId) {
      setPartnerListName(null);
      setLookedUpPartnerId('');
      return;
    }

    let cancelled = false;
    setLookedUpPartnerId(null);

    partnerService
      .getAll({ offset: 0, limit: 1000 })
      .then((res) => {
        if (cancelled) return;
        const match = res.data.find((item) => item.id === reportPartnerId);
        setPartnerListName(match?.name?.trim() || null);
        setLookedUpPartnerId(reportPartnerId);
      })
      .catch(() => {
        if (cancelled) return;
        setPartnerListName(null);
        setLookedUpPartnerId(reportPartnerId);
      });

    return () => {
      cancelled = true;
    };
  }, [reportPartnerId, trimmedBrand]);

  const brandLookupReady = trimmedBrand
    ? true
    : reportPartnerId
      ? lookedUpPartnerId === reportPartnerId
      : lookedUpPartnerId === '';

  const brandName = trimmedBrand || partnerListName;
  const isBrandReady = brandLookupReady;

  useEffect(() => {
    if (!enabled || !reportId) return;

    const canCaptureOpen = captureOpenEvents && isBrandReady;

    const resolvedBrandId = brandName ? reportPartnerId : null;

    const brandProperties = getReportBrandEventProperties({
      brandId: resolvedBrandId,
      brandName,
      reportLang,
    });

    const emitSessionThenOpened = () => {
      if (user) {
        identifyPostHogUser(user);
      }
      registerReportSuperProperties({
        reportId,
        reportDate,
        reportBrand: brandName,
        brandId: resolvedBrandId,
        reportLang,
      });
      if (!canCaptureOpen) return;
      ensureReportReadSession(reportId, brandProperties);
      trackReportOpenEvents(reportId, brandProperties);
    };

    emitSessionThenOpened();
    const stopSession = canCaptureOpen
      ? startReportReadSessionTracking(reportId, brandProperties)
      : () => undefined;

    document.addEventListener('cookieyes_consent_update', emitSessionThenOpened);
    document.addEventListener('cookieyes_banner_load', emitSessionThenOpened);
    window.addEventListener('posthog_capturing_enabled', emitSessionThenOpened);

    return () => {
      document.removeEventListener('cookieyes_consent_update', emitSessionThenOpened);
      document.removeEventListener('cookieyes_banner_load', emitSessionThenOpened);
      window.removeEventListener('posthog_capturing_enabled', emitSessionThenOpened);
      stopSession();
      unregisterReportSuperProperties();
    };
  }, [
    enabled,
    reportId,
    reportDate,
    brandName,
    isBrandReady,
    reportPartnerId,
    reportLang,
    user,
    captureOpenEvents,
  ]);

  return { isBrandReady };
};
